import React, { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Input,
  Button,
  List,
  InputNumber,
  Select,
  Switch,
  Space,
  Typography,
  Row,
  Col,
  Divider,
  message,
  Modal,
  Form
} from 'antd';
import {
  ScanOutlined,
  PlusOutlined,
  DeleteOutlined,
  ShoppingCartOutlined,
  PrinterOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { getProducts, getProductByBarcode } from '../../services/productService';
import './styles/pos-page.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const POSPage = () => {
  const [items, setItems] = useState([]);
  const [barcode, setBarcode] = useState('');
  const [isBarcodeMode, setIsBarcodeMode] = useState(true);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [manualQuantity, setManualQuantity] = useState(1);
  const [manualPrice, setManualPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    address: ''
  });
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [form] = Form.useForm();

  // Load products for manual selection
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      // Replace with your actual API endpoint
      const response = await getProducts();
      const data = response;
      console.log('สินค้า', response.data);
      setProducts( response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error('ไม่สามารถโหลดข้อมูลสินค้าได้');
    }
  };

  const handleBarcodeScan = async (e) => {
    if (e.key === 'Enter' && barcode.trim()) {
      setLoading(true);
      try {
        const product = await getProductByBarcode(barcode.trim());
        if (product) {
          addItemToCart(product, 1, product.price);
          setBarcode('');
          message.success(`เพิ่ม ${product.name} เรียบร้อยแล้ว`);
        } else {
          message.warning('ไม่พบสินค้าที่มีบาร์โค้ดนี้');
        }
      } catch (error) {
        console.error('Error scanning barcode:', error);
        message.error('เกิดข้อผิดพลาดในการสแกนบาร์โค้ด');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleManualAdd = () => {
    if (selectedProduct && manualQuantity > 0 && manualPrice > 0) {
      const product = products.find(p => p.id === selectedProduct);
      addItemToCart(product, manualQuantity, manualPrice);
      setSelectedProduct(null);
      setManualQuantity(1);
      setManualPrice(0);
      message.success(`เพิ่ม ${product.name} เรียบร้อยแล้ว`);
    } else {
      message.warning('กรุณาเลือกสินค้าและระบุจำนวน/ราคา');
    }
  };

  const addItemToCart = (product, quantity, price) => {
    console.log('product', product);
    const existingItemIndex = items.findIndex(item => item.product_id === product.id);
    
    if (existingItemIndex >= 0) {
      const updatedItems = [...items];
      updatedItems[existingItemIndex].quantity += quantity;
      updatedItems[existingItemIndex].total = updatedItems[existingItemIndex].quantity * updatedItems[existingItemIndex].price;
      setItems(updatedItems);
    } else {
      const newItem = {
        id: Date.now(),
        product_id: product.id,
        name: product.name,
        barcode: product.barcode,
        quantity: quantity,
        unit_price: price,
        total: quantity * price
      };
      setItems([...items, newItem]);
    }
  };

  const updateItemQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(itemId);
      return;
    }
    
    const updatedItems = items.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          quantity: newQuantity,
          total: newQuantity * item.unit_price
        };
      }
      return item;
    });
    setItems(updatedItems);
  };

  const updateItemPrice = (itemId, newPrice) => {
    if (newPrice < 0) return;
    
    const updatedItems = items.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          unit_price: newPrice,
          total: item.quantity * newPrice
        };
      }
      return item;
    });
    setItems(updatedItems);
  };

  const removeItem = (itemId) => {
    setItems(items.filter(item => item.id !== itemId));
    message.success('ลบสินค้าเรียบร้อยแล้ว');
  };

  const clearCart = () => {
    Modal.confirm({
      title: 'ยืนยันการล้างรายการ',
      content: 'คุณแน่ใจหรือไม่ที่จะล้างรายการสินค้าทั้งหมด?',
      okText: 'ยืนยัน',
      cancelText: 'ยกเลิก',
      onOk: () => {
        setItems([]);
        message.success('ล้างรายการเรียบร้อยแล้ว');
      }
    });
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + item.total, 0);
  };

  const handleCheckout = () => {
    if (items.length === 0) {
      message.warning('กรุณาเพิ่มสินค้าก่อนชำระเงิน');
      return;
    }
    setShowCustomerModal(true);
  };

  const processPayment = async (values) => {
    try {
      setLoading(true);
      
      // Create receipt data
      const receiptData = {
        customer_name: values.customer_name || 'ลูกค้าทั่วไป',
        customer_phone: values.customer_phone || '',
        customer_address: values.customer_address || '',
        total_amount: calculateTotal(),
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.total
        })),
        created_at: new Date().toISOString()
      };

      // Replace with your actual API endpoint
      const response = await fetch('/api/receipts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(receiptData)
      });

      if (response.ok) {
        message.success('บันทึกใบเสร็จเรียบร้อยแล้ว');
        setItems([]);
        setShowCustomerModal(false);
        form.resetFields();
        // Optionally print receipt here
      } else {
        throw new Error('Failed to save receipt');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      message.error('เกิดข้อผิดพลาดในการบันทึกใบเสร็จ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout className="pos-layout">
      <Content className="pos-content">
        <Row className="pos-container" gutter={16}>
          {/* Left Panel - Input Controls */}
          <Col xs={24} md={8} className="pos-left-panel">
            <Card className="input-panel">
              <Title level={4}>
                <ShoppingCartOutlined /> เพิ่มสินค้า
              </Title>
              
              <Divider />
              
              {/* Barcode Scanner Toggle */}
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div className="scanner-toggle">
                  <Space>
                    <ScanOutlined />
                    <Text>โหมดสแกนบาร์โค้ด</Text>
                    <Switch 
                      checked={isBarcodeMode} 
                      onChange={setIsBarcodeMode}
                      size="small"
                    />
                  </Space>
                </div>

                {/* Barcode Input */}
                {isBarcodeMode && (
                  <div className="barcode-input">
                    <Input
                      size="large"
                      placeholder="สแกนหรือพิมพ์บาร์โค้ดแล้วกด Enter"
                      value={barcode}
                      onChange={(e) => setBarcode(e.target.value)}
                      onKeyPress={handleBarcodeScan}
                      loading={loading}
                      prefix={<ScanOutlined />}
                      autoFocus
                    />
                  </div>
                )}

                {/* Manual Product Selection */}
                {!isBarcodeMode && (
                  <div className="manual-input">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Select
                        size="large"
                        placeholder="เลือกสินค้า"
                        showSearch
                        value={selectedProduct}
                        onChange={(value) => {
                          setSelectedProduct(value);
                          const product = products.find(p => p.id === value);
                          if (product) {
                            setManualPrice(product.selling_price || 0);
                          }
                        }}
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        style={{ width: '100%' }}
                      >
                        {products.map(product => (
                          <Option key={product.id} value={product.id}>
                            {product.name} - {product.barcode}
                          </Option>
                        ))}
                      </Select>
                      
                      <Row gutter={8}>
                        <Col span={12}>
                          <InputNumber
                            size="large"
                            placeholder="จำนวน"
                            min={1}
                            value={manualQuantity}
                            onChange={setManualQuantity}
                            style={{ width: '100%' }}
                          />
                        </Col>
                        <Col span={12}>
                          <InputNumber
                            size="large"
                            placeholder="ราคา"
                            min={0}
                            step={0.01}
                            value={manualPrice}
                            onChange={setManualPrice}
                            style={{ width: '100%' }}
                            formatter={value => `฿ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/฿\s?|(,*)/g, '')}
                          />
                        </Col>
                      </Row>
                      
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={handleManualAdd}
                        block
                      >
                        เพิ่มสินค้า
                      </Button>
                    </Space>
                  </div>
                )}
              </Space>
            </Card>

            {/* Action Buttons */}
            <Card className="action-panel">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  size="large" 
                  icon={<ShoppingCartOutlined />}
                  onClick={handleCheckout}
                  disabled={items.length === 0}
                  block
                >
                  ชำระเงิน ({items.length} รายการ)
                </Button>
                
                <Button 
                  size="large" 
                  icon={<ClearOutlined />}
                  onClick={clearCart}
                  disabled={items.length === 0}
                  block
                >
                  ล้างรายการ
                </Button>
              </Space>
            </Card>
          </Col>

          {/* Center Panel - Items List */}
          <Col xs={24} md={10} className="pos-center-panel">
            <Card className="items-panel">
              <Title level={4}>
                รายการสินค้า ({items.length} รายการ)
              </Title>
              
              <div className="items-list-container">
                {items.length === 0 ? (
                  <div className="empty-cart">
                    <ShoppingCartOutlined className="empty-icon" />
                    <Text type="secondary">ยังไม่มีสินค้าในรายการ</Text>
                  </div>
                ) : (
                  <List
                    className="items-list"
                    dataSource={items}
                    renderItem={(item, index) => (
                      <List.Item className="item-row">
                        <div className="item-content">
                          <div className="item-info">
                            <Text strong className="item-name">{item.name}</Text>
                            <Text type="secondary" className="item-barcode">#{item.barcode}</Text>
                          </div>
                          
                          <div className="item-controls">
                            <Row gutter={8} align="middle">
                              <Col span={6}>
                                <InputNumber
                                  size="small"
                                  min={1}
                                  value={item.quantity}
                                  onChange={(value) => updateItemQuantity(item.id, value)}
                                  style={{ width: '100%' }}
                                />
                              </Col>
                              <Col span={8}>
                                <InputNumber
                                  size="small"
                                  min={0}
                                  step={0.01}
                                  value={item.unit_price}
                                  onChange={(value) => updateItemPrice(item.id, value)}
                                  style={{ width: '100%' }}
                                  formatter={value => `฿${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                  parser={value => value.replace(/฿\s?|(,*)/g, '')}
                                />
                              </Col>
                              <Col span={6}>
                                <Text strong className="item-total">
                                  ฿{item.total.toLocaleString()}
                                </Text>
                              </Col>
                              <Col span={4}>
                                <Button 
                                  type="text" 
                                  danger 
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  onClick={() => removeItem(item.id)}
                                />
                              </Col>
                            </Row>
                          </div>
                        </div>
                      </List.Item>
                    )}
                  />
                )}
              </div>
            </Card>
          </Col>

          {/* Right Panel - Summary */}
          <Col xs={24} md={6} className="pos-right-panel">
            <Card className="summary-panel">
              <Title level={4}>สรุปยอดขาย</Title>
              
              <div className="summary-content">
                <div className="summary-row">
                  <Text>จำนวนสินค้า:</Text>
                  <Text strong>{items.length} รายการ</Text>
                </div>
                
                <div className="summary-row">
                  <Text>จำนวนชิ้น:</Text>
                  <Text strong>{items.reduce((sum, item) => sum + item.quantity, 0)} ชิ้น</Text>
                </div>
                
                <Divider />
                
                <div className="summary-total">
                  <Title level={3} className="total-amount">
                    ฿{calculateTotal().toLocaleString()}
                  </Title>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Customer Info Modal */}
        <Modal
          title="ข้อมูลลูกค้า"
          open={showCustomerModal}
          onCancel={() => setShowCustomerModal(false)}
          footer={null}
          width={500}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={processPayment}
          >
            <Form.Item
              name="customer_name"
              label="ชื่อลูกค้า"
            >
              <Input placeholder="ลูกค้าทั่วไป" />
            </Form.Item>
            
            <Form.Item
              name="customer_phone"
              label="เบอร์โทรศัพท์"
            >
              <Input placeholder="เบอร์โทรศัพท์ลูกค้า" />
            </Form.Item>
            
            <Form.Item
              name="customer_address"
              label="ที่อยู่"
            >
              <Input.TextArea rows={3} placeholder="ที่อยู่ลูกค้า" />
            </Form.Item>
            
            <div className="modal-summary">
              <Title level={4}>ยอดรวม: ฿{calculateTotal().toLocaleString()}</Title>
            </div>
            
            <div className="modal-actions">
              <Space>
                <Button onClick={() => setShowCustomerModal(false)}>
                  ยกเลิก
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  บันทึกใบเสร็จ
                </Button>
              </Space>
            </div>
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
};


export default POSPage;